import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { RankedBars } from '@/shared/ui/charts'
import { CursorPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { fmtNumber, fmtPercent, humanise } from '@/shared/lib/format'
import type { QualityResponse } from '@/shared/api/types'
import { analyticsFlagged, analyticsQuality } from '../api'
import { Money, NotRun, Pct, Pp, Unavailable, Unmatchable } from '../parts'
import { AnalyticsScreen, useAnalyticsFilters } from './shell'

/**
 * Is the data any good, and what is worth a second look.
 *
 * ## A flag is a question, never an accusation
 *
 * Every row on this screen names a thing to check and the threshold it crossed.
 * None of them says anybody did anything. Two beneficiaries sharing a phone
 * number is a household with one phone far more often than it is fraud, and a
 * mobilizer enrolling forty people in a morning is a market day. The screen's
 * job is to put the row in front of somebody who can go and look.
 *
 * ## The denominator of a check is the check's honesty
 *
 * The shared-phone check can only see rows that recorded a phone. If a third of
 * beneficiaries have none, "12 shared numbers" is 12 out of two thirds of the
 * data, and the count of rows the check could not see is printed beside it.
 */
export function ProgramQualityScreen() {
  return (
    <AnalyticsScreen
      title="Delivery quality"
      description="Completeness, how long delivery takes, and rows worth a second look."
      load={(f) => analyticsQuality(f)}
      isEmpty={(d) => (d.completeness?.beneficiaries ?? 0) === 0 && (d.mobilizers?.length ?? 0) === 0}
      exportKind="QUALITY"
    >
      {(d) => <QualityBody d={d} />}
    </AnalyticsScreen>
  )
}

/**
 * "P50" and "P90" are how the server names the cut points; neither means anything to the
 * person reading the screen. The share is the useful half of the label, so say it.
 *
 * An unrecognised code falls through unchanged rather than being guessed at — a wrong plain
 * label is worse than a technical one, because it cannot be spotted as technical.
 */
function waitLabel(percentile: string): string {
  const pct = /^P(\d+)$/.exec(percentile)?.[1]
    ?? (/^0?\.(\d+)$/.test(percentile)
      ? String(Math.round(Number(percentile) * 100))
      : null)
  if (pct === null) return percentile
  if (pct === '50') return 'Half had been served within'
  return `${pct}% had been served within`
}

function QualityBody({ d }: { d: QualityResponse }) {
  const c = d.completeness
  const a = d.anomalies
  const total = c?.beneficiaries ?? 0
  const pctOf = (n: number) => (total > 0 ? n / total : null)

  const gaps = [
    { label: 'Not linked to a voter record', value: c?.unlinked ?? 0 },
    { label: 'No national ID', value: c?.missingNationalId ?? 0 },
    { label: 'No phone number', value: c?.missingPhone ?? 0 },
    { label: 'No post-enrolment reading', value: c?.missingPostEnrolmentReading ?? 0 },
  ].filter((g) => g.value > 0)

  const orphans = [
    { label: 'Milestones with no owner', value: c?.milestonesWithoutOwner ?? 0 },
    { label: 'Disbursements with no beneficiary', value: c?.disbursementsWithoutBeneficiary ?? 0 },
  ].filter((g) => g.value > 0)

  const median = d.timeToDeliver?.find((p) => p.percentile === 'P50' || p.percentile === '0.5')

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard label="Beneficiaries" value={fmtNumber(total)} sub="In scope for these checks" />
        <MetricCard
          label="Unlinked"
          value={fmtNumber(c?.unlinked ?? 0)}
          sub={(
            <span title="A beneficiary with no voter record has no ward, no baseline and no dimensions, so they are invisible to coverage, impact and equity.">
              <Pct value={pctOf(c?.unlinked ?? 0)} digits={0} shortReason /> of beneficiaries
            </span>
          )}
        />
        <MetricCard
          label="Median time to deliver"
          value={median?.days == null ? <Unavailable /> : `${fmtNumber(median.days)} d`}
          sub="From enrolment to the first delivery"
        />
        <MetricCard
          label="Flagged for review"
          value={fmtNumber(a?.flaggedTotal ?? 0)}
          accent
          sub="Rows crossing a threshold — questions, not findings"
        />
      </KpiStrip>

      <Unmatchable count={a?.unmatchableSubjects ?? 0} />

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Panel title="What is missing"
          description="Each of these silently removes a beneficiary from one of the other screens.">
          <RankedBars points={gaps} max={total || undefined}
            emptyMessage="Every beneficiary in scope has a linked record, an ID, a phone and a reading." />
          {orphans.length > 0 && (
            <ul className="mt-3 space-y-1 text-erp-xs text-muted-foreground">
              {orphans.map((o) => (
                <li key={o.label}>{o.label}: <span className="tabular-nums font-medium">{fmtNumber(o.value)}</span></li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="How long people waited"
          description="From the day somebody was enrolled to the day they first received
                       something. Read each line as 'this share of people had been served by
                       then'.">
          {(d.timeToDeliver?.length ?? 0) === 0
            ? <NotRun what="The delivery-time distribution" reason="Nothing has been delivered yet." />
            : (
              <ul className="space-y-1.5">
                {d.timeToDeliver.map((p) => (
                  <li key={p.percentile} className="flex items-baseline justify-between gap-3">
                    <span className="text-erp-xs text-muted-foreground">
                      {waitLabel(p.percentile)}
                    </span>
                    <span className="tabular-nums font-medium">
                      {p.days == null ? <Unavailable short /> : `${fmtNumber(p.days)} days`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          <p className="mt-3 text-erp-xs text-muted-foreground">
            Percentiles, not an average. One programme that took eleven months to hand out
            its first item would drag a mean past every figure that describes the rest.
          </p>
        </Panel>
      </div>

      <Panel title="Mobilizers" bodyClassName="p-0"
        description="Volume, cost and drop rate per mobilizer. A high number is a reason to ask, not a conclusion.">
        <ErpTable head={(
          <>
            <Th>Mobilizer</Th>
            <Th className="text-right">Programmes</Th>
            <Th className="text-right">Enrolments</Th>
            <Th className="text-right">Deliveries</Th>
            <Th className="text-right">Cost each</Th>
            <Th className="text-right">Mean uplift</Th>
            <Th className="text-right">Drop rate</Th>
            <Th className="text-right">Round amounts</Th>
          </>
        )}>
          {(d.mobilizers?.length ?? 0) === 0
            ? <EmptyRow colSpan={8} message="No mobilizer has enrolled anybody under this filter." />
            : d.mobilizers.map((m) => (
              <tr key={m.mobilizerId}>
                <Td className="font-medium">{m.name}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(m.programmes)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(m.enrolments)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(m.deliveries)}</Td>
                <Td className="text-right">
                  <Money value={m.costPerEnrolment} currency={m.currency} mixedReason={m.currencyMixedReason} />
                </Td>
                <Td className="text-right">
                  <div><Pp value={m.meanUplift} reason="Nobody this mobilizer enrolled has been re-assessed." shortReason /></div>
                  {/* `MobilizerPerformanceRow` carries `measured` and no
                      `measurable`. Substituting `enrolments` for the denominator
                      would claim every enrolment could have been measured, which
                      excludes the unlinked and the no-baseline rows — so the
                      count stands alone rather than inventing a fraction. */}
                  <span className="text-erp-xs text-muted-foreground">
                    {fmtNumber(m.measured)} measured
                  </span>
                </Td>
                <Td className="text-right"><Pct value={m.dropRate} digits={0} shortReason /></Td>
                <Td className="text-right">
                  <Pct value={m.roundNumberShare} digits={0} shortReason />
                </Td>
              </tr>
            ))}
        </ErpTable>
        <p className="px-4 py-2 text-erp-xs text-muted-foreground border-t border-border">
          Cost per enrolment here divides the spend a mobilizer's beneficiaries received by
          how many they enrolled. A mobilizer working a high-value programme has a high
          figure because of the programme, not because of them.
        </p>
      </Panel>

      <Panel title="Checks that did not run">
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {a?.velocityRan
            ? (
              <Note tone="info">
                The enrolment-velocity check ran. Unusual bursts appear in the review list below.
              </Note>
            )
            : <NotRun what="The enrolment-velocity check" reason={a?.velocityNotRunReason} />}
          {a?.roundNumbers?.ran
            ? (
              <Note tone="info">
                {fmtPercent(Number(a.roundNumbers.roundShare ?? 0), 0)} of{' '}
                {fmtNumber(a.roundNumbers.valueCount)} amounts are round numbers.
              </Note>
            )
            : <NotRun what="The round-number check" reason={a?.roundNumbers?.notRunReason} />}
        </div>
        {(a?.beneficiariesWithoutPhone ?? 0) > 0 && (
          <p className="mt-3 text-erp-xs text-muted-foreground">
            The shared-phone check covers only the rows that recorded a number.{' '}
            {fmtNumber(a!.beneficiariesWithoutPhone)} beneficiaries have none and are outside it.
          </p>
        )}
      </Panel>

      <FlaggedPanel />
    </div>
  )
}

/**
 * The review list.
 *
 * Every row shows the value and the threshold it crossed, so the reader can
 * judge whether the threshold is the right one for their county rather than
 * taking the flag on trust.
 */
function FlaggedPanel() {
  const f = useAnalyticsFilters()
  const page = useCursorPage((cursor) =>
    analyticsFlagged(f.query, cursor).then((p) => ({
      content: p.rows ?? [],
      nextCursor: p.nextCursor,
      hasMore: p.hasMore,
    })), [JSON.stringify(f.query)])

  return (
    <Panel title="For review" bodyClassName="p-0"
      description="Each row crossed a threshold. None of them is a finding.">
      <ErrorStrip message={page.error} className="m-4" />
      <ErpTable head={(
        <>
          <Th>What</Th>
          <Th>Subject</Th>
          <Th className="text-right">Value</Th>
          <Th className="text-right">Threshold</Th>
          <Th className="text-right">Programmes</Th>
        </>
      )}>
        {page.rows.length === 0 && !page.loading
          ? <EmptyRow colSpan={5} message="Nothing crossed a threshold under this filter." />
          : page.rows.map((r, i) => (
            <tr key={`${r.reasonCode}-${r.subjectId ?? i}`}>
              <Td>{humanise(r.reasonCode)}</Td>
              <Td>
                <span className="text-erp-xs text-muted-foreground">{humanise(r.subjectTypeCode)}</span>
                <div>{r.label}</div>
              </Td>
              <Td className="text-right tabular-nums">{r.value ?? '—'}</Td>
              <Td className="text-right tabular-nums text-muted-foreground">{r.threshold ?? '—'}</Td>
              <Td className="text-right tabular-nums">{fmtNumber(r.programmeCount)}</Td>
            </tr>
          ))}
      </ErpTable>
      <CursorPager page={page.page} count={page.rows.length} hasMore={page.hasMore}
        canGoBack={page.canGoBack} onBack={page.back} onNext={page.next}
        loading={page.loading} unit="rows" />
    </Panel>
  )
}
